import { Viewer } from "../main";
import { SceneLabelTools, AerialLabelTools } from ".";
import { nanoid } from "@reduxjs/toolkit";

export class ImageLabel {
  public id;
  public category;
  public selected = false;
  public completed = false;
  public editing = false;

  constructor(category) {
    this.id = nanoid();
    this.category = category;
  }

  get hasEnoughPoints() {
    console.warn("Not Implemented");
    return false;
  }

  select() {
    this.selected = true;
    this.completed = false;
  }

  unSelect() {
    this.selected = false;
    this.complete();
  }

  edit(id) {
    this.editing = id;
    this.completed = false;
  }

  complete() {
    console.warn("Not Implemented");
  }

  clear() {
    console.warn("Not Implemented");
  }
}

export class LabelCategory {
  public id;
  public name;
  public labels: ImageLabel[] = [];

  constructor(name) {
    this.id = nanoid();
    this.name = name;
  }

  getLabelByID(labelID) {
    return this.labels.find(label => label.id === labelID);
  }

  addNewLabel() {
    console.warn("Not implemented");
    return null;
  }

  deleteLabel(labelID) {
    const label = this.getLabelByID(labelID);
    if (!label) {
      return;
    }

    label.clear();
    const index = this.labels.indexOf(label);
    this.labels.splice(index, 1);
  }

  updateName(name) {
    this.name = name;
  }

  completeLabel(labelID) {
    const label = this.getLabelByID(labelID);
    if (!label) {
      return;
    }

    if (!label.hasEnoughPoints) {
      return false;
    }

    this.unSelectAll();
    label.complete();

    return true;
  }

  unSelectAll() {
    this.labels.forEach(label =>
      label.unSelect()
    );
  }

  selectLabel(labelID) {
    const label = this.getLabelByID(labelID);
    if (!label) {
      return;
    }

    label.select();
  }
}

export class ImageLabelTools {
  viewer: Viewer;
  public orthoLabels: AerialLabelTools;
  public sceneLabels: SceneLabelTools;
  private activeLabeler: AerialLabelTools | SceneLabelTools;
  public categories: LabelCategory[] = [];
  private setCategoryList: Function;

  constructor(viewer: Viewer, props) {
    const {setCategoryList} = props;

    this.viewer = viewer;
    this.orthoLabels = new AerialLabelTools(this, props);
    this.sceneLabels = new SceneLabelTools(this, props);
    this.setCategoryList = setCategoryList;

    this.setCategories();
  }

  setActiveLabelType(type) {
    this.deleteAllCategories();
    this.orthoLabels.setState(false);
    this.sceneLabels.setState(false);

    if (type === "ortho") {
      this.activeLabeler = this.orthoLabels;
      this.orthoLabels.setState(true);
    } else if (type === "scene") {
      this.activeLabeler = this.sceneLabels;
      this.sceneLabels.setState(true);
    } else {
      this.activeLabeler = null;
    }
  }

  checkUniqueName(name) {
    const categoriesWithName = this.categories.filter(category => {
      return category.name.toUpperCase() === name.toUpperCase();
    });

    return (categoriesWithName.length === 0);
  }

  getCategoryByID(id) {
    return this.categories.find(x => x.id === id);
  }

  setCategories() {
    this.setCategoryList([...this.categories]);
  }

  addCategory(name) {
    if (!this.activeLabeler) {
      return;
    }

    let category = this.activeLabeler.getNewCategory(name);
    this.categories.push(category);
    this.setCategories();
  }

  deleteCategory(categoryID) {
    const category = this.getCategoryByID(categoryID);
    if (!category) {
      return;
    }

    category.labels.forEach(label => {
      label.clear();
    });

    const index = this.categories.indexOf(category);
    this.categories.splice(index, 1);
    this.setCategories();
  }

  editCategoryName(categoryID, name) {
    const category = this.getCategoryByID(categoryID);
    if (!category) {
      return;
    }

    category.updateName(name);

    if (this.activeLabeler instanceof SceneLabelTools) {
      this.activeLabeler.updateColor();
    }

    this.setCategories();
    this.activeLabeler.drawAll();
  }

  deleteAllCategories() {
    let categories = [...this.categories];
    categories.forEach(category => {
      this.deleteCategory(category.id);
    });
  }

  addLabel(categoryID) {
    const category = this.getCategoryByID(categoryID);
    if (!category) {
      return;
    }

    let label = category.addNewLabel();
    this.unSelectLabels();
    category.selectLabel(label.id);
    this.setCategories();
  }

  deleteLabel(labelID) {
    this.categories.forEach(category => {
      category.deleteLabel(labelID);
    });

    this.setCategories();
  }

  completeLabel(labelID) {
    let success = false;
    this.categories.forEach(category => {
      success = success || category.completeLabel(labelID);
    });

    this.setCategories();
    if (!success) {
      return;
    }

    this.viewer.showMessage("Added label", {
      variant: "success"
    });
  }

  selectLabel(labelID) {
    this.unSelectLabels();
    this.categories.forEach(category => {
      category.selectLabel(labelID);
    });

    this.setCategories();
  }

  unSelectLabels() {
    this.categories.forEach(category => {
      category.unSelectAll();
    });
  }

  update(changeDetector) {
    this.sceneLabels.update(changeDetector);
  }
}